import type { OnInit } from '@angular/core';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { ShadowSettings } from '@common/types/shadow-settings.type';

import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormField } from '@angular/forms/signals';

import { CSS_RESTRICTIONS } from '@common/constants';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';
import { ELineStyle } from '@common/enums';

import type { ExtendedPositionLegacy, FigureEntity } from '@sites/entity-core';
import type { FigureForm } from '@sites/entity-signal-form';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';
import { SpAnimationSettingsSignalLegacyComponent } from '@components/sp-animation-settings-signal-legacy';
import { SpShadowSettingsSignalLegacyComponent } from '@components/sp-shadow-settings-signal-legacy';

/**
 * Editor control panel for the `figure` widget — fully signal-based. Figure
 * is zero-only, so the panel always renders the zero UI (extended position +
 * extended hide settings); the legacy non-zero fallbacks were dead code and
 * are dropped.
 *
 * Inputs are signal `input()`s, populated by `ControlResolverComponent.setInput`.
 * The form is created in `ngOnInit` via `WebLegacyWidgetSignalFormService.getOrCreateScoped`,
 * shared (ref-counted) with the view component for the same widget.
 */
@Component({
    selector: 'web-figure-control',
    templateUrl: './figure-control.component.html',
    styleUrls: ['./figure-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        FormField,
        WebColorPickerComponent,
        WebWidgetExtendedHideSettingsComponent,
        SpExtendedPositionSignalSettingsComponent,
        SpAnimationSettingsSignalLegacyComponent,
        SpShadowSettingsSignalLegacyComponent,
    ],
})
export class FigureControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<FigureEntity['data']>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });
    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });

    public form!: FigureForm;

    /** Only these shapes expose the contour (border) settings group. */
    public readonly hasContourSettingSet = new Set<string>(['square', 'round']);

    /**
     * Only the rectangular / elliptical shapes expose the box-shadow settings:
     * `box-shadow` (with `spread`) only follows the outline of `square`/`round`.
     * Clip-path shapes keep the fixed `drop-shadow` filter (no `spread`).
     */
    public readonly hasShadowSettingSet = new Set<string>(['square', 'round']);

    protected readonly lineStyle = ELineStyle;
    public readonly SIZE_MIN = CSS_RESTRICTIONS.SIZE_MIN;

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<FigureForm>(this.widget().id, this.destroyRef);
    }

    // --- Shape & colours (color-picker payload unwrapping) ---

    public setBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.form.setBackgroundColor(presetName ?? value);
    }

    public setBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.form.setBorderColor(presetName ?? value);
    }

    // --- Shadow ---

    /** Current shadow bag, falling back to the shared default for old figures. */
    public readonly shadowSettingsValue = computed<ShadowSettings>(
        () => (this.form?.shadowSettings?.()?.value() as ShadowSettings | null | undefined) ?? DEFAULT_SHADOW_SETTINGS
    );

    public setShadowSettings(settings: ShadowSettings): void {
        this.form.setShadowSettings(settings);
    }

    /**
     * Toggling proportions on snaps width to the current height on every
     * device, then flips the flag — batched into one store dispatch.
     * Mirrors the FormGroup control's behaviour.
     */
    public setKeepProportions(event: Event): void {
        const { checked } = (event.target as HTMLInputElement) ?? {};

        this.form.transact(() => {
            if (checked) {
                const position = this.form.position().value();
                this.form.setPosition({
                    ...position,
                    width: position.height,
                    mobileWidth: position.mobileHeight,
                    tabletWidth: position.tabletHeight,
                });
            }
            this.form.setKeepProportions(checked);
        });
    }

    // --- Layout ---

    /** See the video control's `zeroPosition` — same contract. */
    public readonly zeroPosition = computed<ExtendedPositionLegacy | null>(() => {
        if (!this.form) return null;
        return this.form.position().value() as ExtendedPositionLegacy;
    });
}
